import { useEffect, useState } from "react";
import { logout } from "../../api/userlogin";
import styles from "./UserProfile.module.css";
import { useNavigate } from "react-router-dom";
import { useUserContext } from "../UserContext/UserContext";
import { msalInstance } from "../../App";
import { Button, Space, message } from "antd";
import { LogoutOutlined, UserOutlined } from "@ant-design/icons";
import { useMsal } from "@azure/msal-react";
import { handleLogin as handleLoginFlow } from "../../utils/auth";

// Define a type for userProfile
interface UserProfile {
  displayName: string;
}

function UserProfile() {
  const { userProfile, setUserProfile } = useUserContext();
  const [loading, setLoading] = useState(true);
  const [isLoggingIn, setIsLoggingIn] = useState(false);
  const navigate = useNavigate();
  const { instance, accounts } = useMsal();

  useEffect(() => {
    // Profile is now managed centrally in App.tsx
    setLoading(false);
  }, []);

  const handleLogin = async () => {
    setIsLoggingIn(true);
    try {
      const isContainer = !window.location.hostname.includes('localhost');
      if (isContainer) {
        message.info("Redirecting to login...");
      }
      
      await handleLoginFlow(instance, accounts, setUserProfile, navigate);
    } catch (error: any) {
      console.error("Login error:", error);
      message.error(error.message || "Login failed. Please try again.");
    } finally {
      setIsLoggingIn(false);
    }
  };

  const handleLogout = async () => {
    // Clear the user profile from context
    setUserProfile(null);

    // Clear session storage (tokens and MSAL state)
    sessionStorage.removeItem("access_token");
    sessionStorage.clear(); // Clear other MSAL related data

    // Ensure MSAL session is cleared and the user is logged out
    const accounts = msalInstance.getAllAccounts();
    if (accounts.length > 0) {
      // logout() uses logoutRedirect which will redirect to "/" automatically
      // No need to call navigate() separately
      await logout(msalInstance);
    } else {
      // If no MSAL accounts, just redirect manually
      navigate("/");
    }
  };

  if (loading) {
    return <span>Loading profile...</span>;
  }

  if (!userProfile) {
    return (
      <Button 
        type="primary" 
        onClick={handleLogin}
        className={styles.loginButton}
        loading={isLoggingIn}
        disabled={isLoggingIn}
      >
        {isLoggingIn ? "Signing in..." : "Login"}
      </Button>
    );
  }

  return (
    <Space align="center" size="small">
      <span className={styles.welcomeText}>
        <UserOutlined style={{ marginRight: 4 }} />
        Welcome, {userProfile.displayName}!
      </span>
      <Button
        type="text"
        icon={<LogoutOutlined />}
        onClick={handleLogout}
        className={styles.logoutButton}
        title="Logout"
      />
    </Space>
  );
}

export default UserProfile;
